import {
  del,
  get,
  axiosConfig,
  type RequestConfig,
} from "../shared/api-client";
import type {
  ApiItemResponse,
  ListQuery,
  PaginatedResult,
} from "@project/types/location";
import type { Event, EventPayload } from "@project/types/event";
import {
  JIVANDHARA_BASE,
  SILENT_LIST,
  fetchListPage,
  unwrapItem,
} from "./list-api-helpers";

const BASE = `${JIVANDHARA_BASE}/events`;
const SILENT: RequestConfig = { showNotification: false };

function appendIfPresent(fd: FormData, key: string, value: unknown) {
  if (value === undefined || value === null || value === "") {
    return;
  }
  if (value instanceof File) {
    fd.append(key, value);
    return;
  }
  fd.append(key, String(value));
}

function toFormData(payload: Partial<EventPayload>): FormData {
  const fd = new FormData();
  appendIfPresent(fd, "event_title", payload.event_title);
  appendIfPresent(fd, "category", payload.category);
  appendIfPresent(fd, "status", payload.status);
  appendIfPresent(fd, "date", payload.date);
  appendIfPresent(fd, "start_time", payload.start_time);
  appendIfPresent(fd, "end_time", payload.end_time);
  appendIfPresent(fd, "venue", payload.venue);
  appendIfPresent(fd, "address", payload.address);
  appendIfPresent(fd, "max_seat_capacity", payload.max_seat_capacity);
  appendIfPresent(fd, "short_summary", payload.short_summary);
  appendIfPresent(fd, "description", payload.description);
  appendIfPresent(fd, "start_date", payload.start_date);
  appendIfPresent(fd, "end_date", payload.end_date);

  if (payload.image instanceof File) {
    fd.append("image", payload.image);
  }
  return fd;
}

class EventService {
  listPage = (query: ListQuery = {}): Promise<PaginatedResult<Event>> =>
    fetchListPage<Event>(`${BASE}/`, query);

  get = async (id: number): Promise<Event | undefined> => {
    const data = await get<ApiItemResponse<Event> | Event>(
      `${BASE}/${id}/`,
      SILENT_LIST
    );
    return unwrapItem(data);
  };

  create = async (payload: EventPayload): Promise<Event> => {
    const response = await axiosConfig.post<ApiItemResponse<Event> | Event>(
      `${BASE}/`,
      toFormData(payload),
      {
        ...SILENT,
        headers: { "Content-Type": "multipart/form-data" },
      }
    );
    const item = unwrapItem(response.data);
    if (!item) {
      throw new Error("Failed to create event.");
    }
    return item;
  };

  update = async (
    id: number,
    payload: Partial<EventPayload>
  ): Promise<Event | undefined> => {
    const response = await axiosConfig.patch<ApiItemResponse<Event> | Event>(
      `${BASE}/${id}/`,
      toFormData(payload),
      {
        ...SILENT,
        headers: { "Content-Type": "multipart/form-data" },
      }
    );
    return unwrapItem(response.data);
  };

  delete = async (id: number): Promise<void> => {
    await del(`${BASE}/${id}/`, SILENT);
  };
}

export const eventService = new EventService();
